import { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { ScrollArea } from "./ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Upload, FileText, Trash2, ExternalLink, File, Image, FileSpreadsheet, Download, Eye, X, HardDrive, Cloud, Server, Loader2 } from "lucide-react";
import { toast } from "sonner";
// 29/09/2026 — date gg/mm/aaaa da un'unica fonte (qui si usava date-fns).
import { formatDate } from "../lib/format";
import DocumentConfirmDialog from "./DocumentConfirmDialog";
import { useConferma } from "./ConfirmProvider";

export function DocumentsDialog({ open, onOpenChange, entityType, entityId, entityName, onDocumentsChange }) {
  const [documents, setDocuments] = useState([]);
  const conferma = useConferma();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [storageOption, setStorageOption] = useState("both");
  const [driveConnected, setDriveConnected] = useState(false);
  const [localConfigured, setLocalConfigured] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      fetchDocuments();
      checkStorageStatus();
    }
  }, [open, entityId]);

  const checkStorageStatus = async () => {
    try {
      // Check MEGA status
      const driveResponse = await axios.get('/api/drive/status');
      setDriveConnected(driveResponse.data.connected);
      
      // Check local storage status
      const storageResponse = await axios.get('/api/storage/status');
      setLocalConfigured(storageResponse.data.local_configured);
      
      // Set default based on what's available
      if (driveResponse.data.connected && storageResponse.data.local_configured) {
        setStorageOption("both");
      } else if (storageResponse.data.local_configured) {
        setStorageOption("local");
      } else if (driveResponse.data.connected) {
        setStorageOption("drive");
      }
    } catch (error) {
      console.error("Error checking storage status:", error);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`/api/documents?entity_type=${entityType}&entity_id=${entityId}`);
      setDocuments(response.data);
    } catch (error) {
      console.error("Error fetching documents:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Step 1: Analyze with AI
    setAnalyzing(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `/api/documents/analyze?entity_type=${entityType}&entity_id=${entityId}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      // Store the file and analysis result, show confirmation dialog
      setPendingFile(file);
      setAnalysisResult(response.data);
      setConfirmDialog(true);
    } catch (error) {
      const detail = error.response?.data?.detail || "Errore nell'analisi del documento";
      toast.error(detail);
    } finally {
      setAnalyzing(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleConfirmUpload = async (confirmedType) => {
    if (!pendingFile) return;
    
    // Step 2: Upload with confirmed type
    setConfirming(true);
    const formData = new FormData();
    formData.append('file', pendingFile);
    
    try {
      const response = await axios.post(
        `/api/documents/upload?entity_type=${entityType}&entity_id=${entityId}&storage=${storageOption}&auto_rename=true&confirmed_type=${encodeURIComponent(confirmedType)}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      if (response.data.drive_warning) {
        toast.warning(response.data.drive_warning, { duration: 5000 });
      }
      
      toast.success(`Documento salvato come: ${confirmedType.replace(/_/g, ' ')}`);
      setConfirmDialog(false);
      setPendingFile(null);
      setAnalysisResult(null);
      fetchDocuments();
      if (onDocumentsChange) onDocumentsChange();
    } catch (error) {
      const detail = error.response?.data?.detail || "Errore nel caricamento";
      toast.error(detail);
    } finally {
      setConfirming(false);
    }
  };

  const handleDelete = async (documentId) => {
    if (!(await conferma({
      titolo: "Sei sicuro di voler eliminare questo documento?",
      descrizione: "Il documento verrà eliminato definitivamente.",
    }))) return;
    try {
      await axios.delete(`/api/documents/${documentId}`);
      toast.success("Documento eliminato");
      fetchDocuments();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await axios.get(`/api/documents/download/${doc.document_id}`, {
        responseType: 'blob'
      });
      
      // Check if it's a redirect response
      if (response.data.redirect) {
        window.open(response.data.redirect, '_blank');
        return;
      }
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Download avviato");
    } catch (error) {
      // Try MEGA URL as fallback
      if (doc.drive_url) {
        window.open(doc.drive_url, '_blank');
      } else {
        toast.error("Errore nel download");
      }
    }
  };

  const handlePreview = async (doc) => {
    try {
      // For images and PDFs, get preview from API
      const response = await axios.get(`/api/documents/preview/${doc.document_id}`, {
        responseType: 'blob'
      });
      
      // Check if it's JSON response (external URL)
      const contentType = response.headers['content-type'];
      if (contentType && contentType.includes('application/json')) {
        const text = await response.data.text();
        const data = JSON.parse(text);
        if (data.preview_url) {
          window.open(data.preview_url, '_blank');
          return;
        }
        if (data.type === 'none') {
          toast.info("Anteprima non disponibile per questo tipo di file");
          return;
        }
      }
      
      // Create preview URL
      const url = window.URL.createObjectURL(new Blob([response.data], { type: doc.mime_type }));
      setPreviewUrl(url);
      setPreviewDoc(doc);
    } catch (error) {
      // Try MEGA URL as fallback
      if (doc.drive_url) {
        window.open(doc.drive_url, '_blank');
      } else {
        toast.error("Anteprima non disponibile");
      }
    }
  };

  const closePreview = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setPreviewDoc(null);
  };

  const getFileIcon = (mimeType) => {
    if (mimeType?.startsWith('image/')) return <Image className="h-5 w-5 text-blue-500" />;
    if (mimeType?.includes('pdf')) return <FileText className="h-5 w-5 text-red-500" />;
    if (mimeType?.includes('spreadsheet') || mimeType?.includes('excel')) return <FileSpreadsheet className="h-5 w-5 text-green-500" />;
    return <File className="h-5 w-5 text-gray-500" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const canPreview = (mimeType) => {
    return mimeType?.startsWith('image/') || mimeType?.includes('pdf');
  };

  const getStorageOptions = () => {
    const options = [];
    if (localConfigured) {
      options.push({ value: "local", label: "Solo Locale", icon: HardDrive });
    }
    if (driveConnected) {
      options.push({ value: "drive", label: "Solo MEGA", icon: Cloud });
    }
    if (localConfigured && driveConnected) {
      options.push({ value: "both", label: "Entrambi", icon: Server });
    }
    return options;
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Documenti - {entityName}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Storage Selection */}
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium whitespace-nowrap">Salva in:</span>
              <Select value={storageOption} onValueChange={setStorageOption}>
                <SelectTrigger className="flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {localConfigured && (
                    <SelectItem value="local">
                      <div className="flex items-center gap-2">
                        <HardDrive className="h-4 w-4" />
                        Solo Locale
                      </div>
                    </SelectItem>
                  )}
                  {driveConnected && (
                    <SelectItem value="drive">
                      <div className="flex items-center gap-2">
                        <Cloud className="h-4 w-4" />
                        Solo MEGA
                      </div>
                    </SelectItem>
                  )}
                  {localConfigured && driveConnected && (
                    <SelectItem value="both">
                      <div className="flex items-center gap-2">
                        <Server className="h-4 w-4" />
                        Entrambi (Locale + MEGA)
                      </div>
                    </SelectItem>
                  )}
                  {!localConfigured && !driveConnected && (
                    <SelectItem value="none" disabled>
                      Nessuno storage configurato
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Status indicators */}
            <div className="flex gap-4 text-xs">
              <div className={`flex items-center gap-1 ${localConfigured ? 'text-green-600' : 'text-muted-foreground'}`}>
                <HardDrive className="h-3 w-3" />
                Locale: {localConfigured ? '✓ Configurato' : '✗ Non configurato'}
              </div>
              <div className={`flex items-center gap-1 ${driveConnected ? 'text-blue-600' : 'text-muted-foreground'}`}>
                <Cloud className="h-3 w-3" />
                Drive: {driveConnected ? '✓ Connesso' : '✗ Non connesso'}
              </div>
            </div>

            {/* Upload Button */}
            <div className="flex items-center gap-2">
              <Input
                ref={fileInputRef}
                type="file"
                onChange={handleFileUpload}
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || analyzing || (!localConfigured && !driveConnected)}
                className="w-full"
                data-testid="upload-document-btn"
              >
                {analyzing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Analisi AI in corso...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Carica Documento
                  </>
                )}
              </Button>
            </div>

            <p className="text-xs text-muted-foreground">
              Formati supportati: PDF, JPG, PNG, DOC, DOCX, XLS, XLSX
              <br />
              <span className="text-primary">🤖 L'IA riconoscerà automaticamente il tipo di documento e lo rinominerà</span>
            </p>

            {/* Documents List */}
            <ScrollArea className="h-[300px]">
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : documents.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Nessun documento caricato
                </div>
              ) : (
                <div className="space-y-2">
                  {documents.map((doc) => (
                    <div
                      key={doc.document_id}
                      className="flex items-center gap-3 p-3 bg-muted/50 rounded-sm border border-border hover:bg-muted/80 transition-colors"
                    >
                      {getFileIcon(doc.mime_type)}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{doc.name}</p>
                        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                          <span>{formatFileSize(doc.size)}</span>
                          <span>•</span>
                          <span>{formatDate(doc.created_at)}</span>
                          {doc.document_type && (
                            <>
                              <span>•</span>
                              <span className="px-1.5 py-0.5 bg-primary/10 text-primary rounded text-[10px] font-medium">
                                {doc.document_type.replace(/_/g, ' ')}
                              </span>
                            </>
                          )}
                          {doc.local_path && (
                            <>
                              <span>•</span>
                              <span className="text-green-600 flex items-center gap-1">
                                <HardDrive className="h-3 w-3" />
                              </span>
                            </>
                          )}
                          {doc.drive_url && (
                            <>
                              <span>•</span>
                              <span className="text-blue-600 flex items-center gap-1">
                                <Cloud className="h-3 w-3" />
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        {/* Preview Button */}
                        {canPreview(doc.mime_type) && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handlePreview(doc)}
                            title="Anteprima"
                          >
                            <Eye className="h-4 w-4 text-primary" />
                          </Button>
                        )}
                        {/* Download Button */}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDownload(doc)}
                          title="Scarica"
                        >
                          <Download className="h-4 w-4 text-green-600" />
                        </Button>
                        {/* External Link (MEGA) */}
                        {doc.drive_url && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => window.open(doc.drive_url, '_blank')}
                            title="Apri su MEGA"
                          >
                            <ExternalLink className="h-4 w-4 text-blue-600" />
                          </Button>
                        )}
                        {/* Delete Button */}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(doc.document_id)}
                          className="text-destructive hover:text-destructive"
                          title="Elimina"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      {previewDoc && previewUrl && (
        <Dialog open={true} onOpenChange={closePreview}>
          <DialogContent className="sm:max-w-[90vw] sm:max-h-[90vh] p-0">
            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                onClick={closePreview}
                className="absolute top-2 right-2 z-10 bg-background/80 hover:bg-background"
               aria-label="Chiudi">
                <X className="h-5 w-5" />
              </Button>
              <div className="p-4">
                <h3 className="font-medium mb-4 pr-10">{previewDoc.name}</h3>
                {previewDoc.mime_type?.startsWith('image/') ? (
                  <img
                    src={previewUrl}
                    alt={previewDoc.name}
                    className="max-w-full max-h-[70vh] mx-auto rounded-sm"
                  />
                ) : previewDoc.mime_type?.includes('pdf') ? (
                  <iframe
                    src={previewUrl}
                    title={previewDoc.name}
                    className="w-full h-[70vh] rounded-sm border"
                  />
                ) : null}
              </div>
              <div className="p-4 border-t flex justify-end gap-2">
                <Button variant="outline" onClick={closePreview}>
                  Chiudi
                </Button>
                <Button onClick={() => handleDownload(previewDoc)}>
                  <Download className="h-4 w-4 mr-2" />
                  Scarica
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Document Type Confirmation Dialog */}
      <DocumentConfirmDialog
        open={confirmDialog}
        onOpenChange={(open) => {
          setConfirmDialog(open);
          if (!open) {
            setPendingFile(null);
            setAnalysisResult(null);
          }
        }}
        analysisData={analysisResult}
        onConfirm={handleConfirmUpload}
        confirming={confirming}
      />
    </>
  );
}

export default DocumentsDialog;
